<template>
	<div class="rt">
		<div class="rt-top">
			<nav class="rt-types" aria-label="Recommendation type">
				<button
					v-for="type in types"
					:key="type.key"
					type="button"
					class="rt-type"
					:class="{ 'rt-type--active': activeType === type.key }"
					@click="activeType = type.key"
				>
					{{ type.label }}
					<span v-if="count(type.key)" class="rt-count">{{ count(type.key) }}</span>
				</button>
			</nav>
			<button type="button" class="rt-refresh" :disabled="loading" @click="$emit('refresh')">
				{{ loading ? "Loading…" : "Refresh" }}
			</button>
		</div>

		<p v-if="error" class="rt-note rt-note--error">{{ error }}</p>
		<p v-else-if="loading && !recommendations" class="rt-note">Ranking recommendations…</p>
		<template v-else-if="current">
			<p v-if="!current.available" class="rt-note rt-note--error">{{ typeLabel }} could not load this time. Refresh to try again.</p>
			<p v-else-if="!current.items.length" class="rt-note">No {{ typeLabel.toLowerCase() }} to recommend yet. Browse a few to build your profile.</p>
			<template v-else>
				<p v-if="current.partial" class="rt-note">Some searches timed out, so this list may be short.</p>
				<section v-for="group in groups" :key="group.key" class="rt-group" :class="'rt-group--' + group.key">
					<h3 v-if="groups.length > 1">{{ group.title }}</h3>
					<p v-if="groups.length > 1" class="rt-group__hint">{{ group.hint }}</p>
					<ol class="rt-list">
						<li v-for="item in group.items" :key="item.itemId" class="rt-item">
							<div class="rt-item__main">
								<span class="rt-item__title">{{ item.title || item.itemId }}</span>
								<span class="rt-item__tags">{{ matchLabel(item) }}</span>
								<span v-if="item.title" class="rt-item__id">{{ item.itemId }}</span>
							</div>
							<span class="rt-match" :title="'Match ' + percent(item) + '%'">{{ percent(item) }}%</span>
						</li>
					</ol>
				</section>
			</template>
		</template>
		<p v-else class="rt-note">Recommendations load when you open this tab.</p>
	</div>
</template>

<script>
import { tagName } from "./hudFormat";

export default {
	name: "HudRecommendationsTab",
	props: {
		recommendations: { type: Object, default: null },
		domains: { type: Array, default: () => [] },
		loading: { type: Boolean, default: false },
		error: { type: String, default: null },
	},
	data() {
		return {
			activeType: "job",
			types: [
				{ key: "job", label: "Jobs" },
				{ key: "assessment", label: "Assessments" },
				{ key: "course", label: "Courses" },
			],
		};
	},
	computed: {
		current() {
			return this.recommendations && this.recommendations[this.activeType];
		},
		typeLabel() {
			return this.types.find((type) => type.key === this.activeType).label;
		},
		names() {
			const map = {};
			this.domains.forEach((domain) => {
				map[domain.tag] = domain.name;
				(domain.subdomains || []).forEach((sub) => {
					map[sub.tag] = sub.name;
				});
			});
			return map;
		},
		groups() {
			const items = (this.current && this.current.items) || [];
			const recent = items.filter((item) => item.recent);
			const rest = items.filter((item) => !item.recent);
			// Without a recent slot, one plain list reads better than a single labelled group.
			if (!recent.length) return [{ key: "all", items: rest }];
			const groups = [{ key: "recent", title: "From your recent activity", hint: "A new direction in what you viewed lately.", items: recent }];
			if (rest.length) groups.push({ key: "long", title: "From your long-term interests", hint: "Built from everything you have done.", items: rest });
			return groups;
		},
	},
	methods: {
		count(type) {
			const block = this.recommendations && this.recommendations[type];
			return block && block.items ? block.items.length : 0;
		},
		percent(item) {
			const value = Number(item.matchPercentage);
			return isFinite(value) ? Math.round(Math.min(1, Math.max(0, value)) * 100) : 0;
		},
		matchLabel(item) {
			const match = item.match || {};
			const domain = this.names[match.domainTag] || tagName(match.domainTag);
			if (!match.subdomainTag) return domain;
			return `${domain} › ${this.names[match.subdomainTag] || tagName(match.subdomainTag)}`;
		},
	},
};
</script>

<style scoped>
.rt {
	padding: 12px 16px 20px;
}
.rt-top {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
	margin-bottom: 12px;
}
.rt-types {
	display: flex;
	gap: 6px;
}
.rt-type {
	padding: 5px 10px;
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	background: #fff;
	color: #464667;
	font: 600 11.5px/1.2 "Montserrat", sans-serif;
	cursor: pointer;
}
.rt-type--active {
	border-color: #20639b;
	background: #20639b;
	color: #fff;
}
.rt-count {
	margin-left: 3px;
	opacity: 0.75;
}
.rt-refresh {
	padding: 5px 10px;
	border: 1px solid #20639b;
	border-radius: 8px;
	background: #fff;
	color: #20639b;
	font: 600 12px "Montserrat", sans-serif;
	cursor: pointer;
}
.rt-refresh:disabled {
	opacity: 0.5;
	cursor: default;
}
.rt-type:focus-visible,
.rt-refresh:focus-visible {
	outline: 2px solid #4a7eff;
	outline-offset: 2px;
}
.rt-note {
	font-size: 13px;
	color: #72738e;
}
.rt-note--error {
	color: #b3261e;
}

.rt-group {
	margin-bottom: 14px;
}
.rt-group h3 {
	margin: 0;
	font: 700 12.5px "Montserrat", sans-serif;
	color: #1a4164;
}
.rt-group__hint {
	margin: 2px 0 8px;
	font-size: 11.5px;
	color: #72738e;
}
.rt-list {
	margin: 0;
	padding: 0;
	list-style: none;
}
.rt-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	padding: 10px 12px;
	margin-bottom: 6px;
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	background: #fff;
}
.rt-group--recent .rt-item {
	border-color: #c4dcf6;
	border-left: 3px solid #4a7eff;
	background: #f6f9fb;
}
.rt-item__main {
	display: flex;
	flex-direction: column;
	min-width: 0;
}
.rt-item__title {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font: 700 13px "Montserrat", sans-serif;
	color: #1a4164;
}
.rt-item__tags {
	font-size: 12px;
	color: #464667;
}
.rt-item__id {
	font-size: 11px;
	color: #72738e;
}
.rt-match {
	flex: none;
	font: 700 14px "Montserrat", sans-serif;
	color: #20639b;
	font-variant-numeric: tabular-nums;
}
</style>
